import React, { useState, useEffect, useRef } from "react";
import UserInputTextarea from "./UserInputTextarea";
import SendStopButtons from "./SendStopButtons";
import FileUploadSidebar from "../../chatwindow/fileUploadSidebar/FileUploadSidebar";
import FileCard from "./FilesSelectComponent/FileCard";
import Tools from "./Tools/Tools";
import "./UserInput.css";
import { IconButton, Snackbar, Alert } from "@mui/material";
import FilePresentIcon from '@mui/icons-material/FilePresent';
import UpdateChatName from "../../../utils/database/UpdateChatName";
import useUserDetails from "../../../utils/auth/UserDetails";

// Tool metadata for both built-in and custom tools
const toolMapping = {
  "Search the Web": { icon: "pi pi-search", info: "Search the internet for information." },
  "Generate Picture": { icon: "pi pi-image", info: "Generate images based on text prompts." },
  "Search Documents": { icon: "pi pi-folder", info: "Search within uploaded documents." },
  "Graph Interpretation": { icon: "pi pi-chart-bar", info: "Generate Graph based on text prompts." },
  "Generate Presentation": { icon: "pi pi-file-export", info: "Generate a PowerPoint presentation." },
  "Generate Document": { icon: "pi pi-file-word", info: "Generate a Word document." },
  "Generate Spreadsheet": { icon: "pi pi-file-excel", info: "Generate an Excel spreadsheet." },
};

const customToolIconByType = {
  "postgresql": "pi pi-database",
  "blob-storage": "pi pi-cloud",
  "databricks": "pi pi-server",
  "rest-api": "pi pi-link",
};

const buildToolObject = (name) => {
  const mapped = toolMapping[name] || { icon: "pi pi-question-circle", info: name };
  return { name, icon: mapped.icon, info: mapped.info };
};

const buildCustomToolObject = (customTool) => {
  const icon = customToolIconByType[customTool?.type] || "pi pi-cog";
  return {
    name: customTool.name,
    icon,
    info: customTool.description || `Custom ${customTool.type || "tool"}`,
  };
};

// Compute the tools that should be auto-selected when a chat opens.
// Default DoAssist chat ("DONA") starts with no pre-selection — tools get added
// on file upload, intent detection, or fall back to Search the Web on send.
// User-created extensions pre-select whatever they were configured with.
const getDefaultToolsForExtension = (extension, extensionTools, customTools) => {
  if (!extension || extension.id === "DONA") return [];
  const result = [];
  if (extensionTools && extensionTools.length > 0) {
    extensionTools.forEach((name) => result.push(buildToolObject(name)));
  }
  if (customTools && customTools.length > 0) {
    customTools.forEach((ct) => result.push(buildCustomToolObject(ct)));
  }
  return result;
};

const UserInput = ({
  processMessage,
  stopStreaming,
  isStreaming,
  extension,
  chatId,
  setSelectedDocumentIds,
  selectedDocumentIds,
  extensionTools = [],
  customTools = [],
  oldChats,
  setOldChats,
  selectedChatObject
}) => {
  const [userInput, setUserInput] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showTools, setShowTools] = useState(false);

  const [selectedTools, setSelectedTools] = useState(() =>
    getDefaultToolsForExtension(extension, extensionTools, customTools)
  );
  // Track whether "Search Documents" was added by the file-upload effect (so we
  // can pull it back out if all files get cleared, without undoing manual picks).
  const searchDocsAutoAddedRef = useRef(false);

  const [showToolWarning, setShowToolWarning] = useState(false);

  const userDetails = useUserDetails();

  // Re-pick defaults whenever the user opens a different chat or extension
  useEffect(() => {
    setSelectedTools(getDefaultToolsForExtension(extension, extensionTools, customTools));
    searchDocsAutoAddedRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extension?.id, chatId]);

  // Auto-toggle "Search Documents" based on whether files are attached
  useEffect(() => {
    const hasFiles =
      (selectedFiles && selectedFiles.length > 0) ||
      (selectedDocumentIds && selectedDocumentIds.length > 0);
    setSelectedTools((prev) => {
      const alreadySelected = prev.some((t) => t.name === "Search Documents");
      if (hasFiles && !alreadySelected) {
        searchDocsAutoAddedRef.current = true;
        return [...prev, buildToolObject("Search Documents")];
      }
      if (!hasFiles && alreadySelected && searchDocsAutoAddedRef.current) {
        searchDocsAutoAddedRef.current = false;
        return prev.filter((t) => t.name !== "Search Documents");
      }
      return prev;
    });
  }, [selectedFiles, selectedDocumentIds]);

  useEffect(() => {
    if (selectedTools.length) {
      sessionStorage.setItem("selectedTools", JSON.stringify(selectedTools));
    } else {
      sessionStorage.removeItem("selectedTools");
    }
  }, [selectedTools]);

  const handleUserInput = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!userInput.trim()) return;

      // Send whatever tools are currently selected (could be empty for DONA).
      // Backend will run AI classification when no tool is set.
      const toolsToSend = selectedTools;

      processMessage(
        userInput,
        toolsToSend,
        selectedFiles,
        extension.name,
        selectedDocumentIds,
        toolsToSend
      );
      if (selectedChatObject?.chatName === "NewChat" && !selectedChatObject?.isManuallyRenamed) {
        handleRenameChat(chatId, userInput);
      }
      setUserInput("");
      setSelectedFiles([]);
      setSelectedDocumentIds && setSelectedDocumentIds([]);
    }
  };

  const handleRenameChat = async (chatId, newChatName) => {
    // Get the current chat from oldChats to check its current name
    const currentChat = oldChats.find(c => c.chatId === chatId);
    
    // Only rename if the current chat name is still "NewChat"
    if (currentChat?.chatName !== "NewChat") {
      return;
    }
    
    const shortenedChatName = newChatName.slice(0, 20);
    const status = await UpdateChatName(chatId, userDetails.userId, shortenedChatName, false);
    if (status) {
      setOldChats((prevChats) =>
        prevChats.map((c) =>
          c.chatId === chatId ? { ...c, chatName: shortenedChatName, isManuallyRenamed: false } : c
        )
      );
    }
  };

  const handleSendClick = () => {
    if (!userInput.trim()) return;

    // Send whatever tools are currently selected (could be empty for DONA).
    // Backend will run AI classification when no tool is set.
    const toolsToSend = selectedTools;

    processMessage(
      userInput,
      toolsToSend,
      selectedFiles,
      extension.name,
      selectedDocumentIds,
      toolsToSend
    );
    if (selectedChatObject?.chatName === "NewChat" && !selectedChatObject?.isManuallyRenamed) {
      handleRenameChat(chatId, userInput);
    }
    setUserInput("");
    setSelectedFiles([]);
    setSelectedDocumentIds && setSelectedDocumentIds([]);
  };

  const handleStreaming = () => {
    if (isStreaming) {
      stopStreaming();
    }
  };

  const handleClearMultipleFiles = (fileToRemove) => {
    setSelectedFiles((prevFiles) =>
      prevFiles.filter((file) => file.name !== fileToRemove.name)
    );
  };

  const handleFileChangeFromSidebar = (newFile) => {
    setSelectedFiles((prevFiles) => {
      const isFileAlreadySelected = prevFiles.some(
        (file) => file.name === newFile.name
      );
      if (isFileAlreadySelected) {
        return prevFiles;
      }
      if (prevFiles.length >= 10) {
        return prevFiles;
      }
      return [...prevFiles, newFile];
    });
  };

  const handleFileIconClick = () => {
    setShowSidebar(true);
  };

  const handleToolToggle = (tool) => {
    setSelectedTools((prev) => {
      const exists = prev.find((t) => t.name === tool.name);
      if (exists) {
        return prev.filter((t) => t.name !== tool.name);
      } else {
        return [...prev, tool];
      }
    });
  };

  return (
    <div id="interaction" className="p-d-flex p-ai-start">
      <div className="file-cards-container">
        {selectedFiles.map((file, index) => (
          <FileCard
            key={`${file.name}-${index}`}
            fileName={file.name}
            onRemove={() => handleClearMultipleFiles(file)}
          />
        ))}
      </div>

      <div className="textarea-container">
        <UserInputTextarea
          userInput={userInput}
          setUserInput={setUserInput}
          handleUserInput={handleUserInput}
        />

        <div className="icon-container">
          <div className="tool-icon-container">
            <IconButton onClick={handleFileIconClick}>
              <FilePresentIcon />
            </IconButton>

            <Tools
              visible={showTools}
              onClose={() => setShowTools(false)}
              onToolSelect={handleToolToggle}
              selectedTools={selectedTools}
              extensionTools={extensionTools}
              customTools={customTools}
            />
          </div>

          <div>
            <SendStopButtons
              isStreaming={isStreaming}
              handleSendClick={handleSendClick}
              handleStreaming={handleStreaming}
            />
          </div>
        </div>
      </div>

      <FileUploadSidebar
        visible={showSidebar}
        onHide={() => setShowSidebar(false)}
        chatId={chatId}
        extension={extension}
        handleFileChangeFromSidebar={handleFileChangeFromSidebar}
        setSelectedDocumentIds={setSelectedDocumentIds}
      />

      <Snackbar
        open={showToolWarning}
        autoHideDuration={4000}
        onClose={() => setShowToolWarning(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowToolWarning(false)}
          severity="warning"
          variant="filled"
          sx={{ width: "100%", fontWeight: 600 }}
        >
          Please select a tool below (like Search the Web or Search Documents) to send your message
        </Alert>
      </Snackbar>
    </div>
  );
};

export default UserInput;
